import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { Tariff } from '../../../models/tariff.model';
import { TariffService } from '../../../services/tariff.service';
import { ToastrService } from 'ngx-toastr';
import { BuildingType, CustomerType, ServiceType } from '../../../models/customer.model';
import { CustomerService } from '../../../services/customer.service';
import { OptionObj } from 'shared-lib';
import { UtilitySharedService } from '../../../shared/_services/utility-shared.service';

@Component({
  selector: 'app-crud-tariff',
  standalone: false,
  templateUrl: './crud-tariff.component.html',
  styleUrl: './crud-tariff.component.scss',
})
export class CrudTariffComponent implements OnInit, OnChanges {
  @ViewChild('filterTemplate', { static: true })
  filterTemplate!: TemplateRef<any>;

  @Input() downloadClicked: boolean = false;
  @Input() reloadTableInput: boolean = false;

  count: number = 0;
  currentPage: number = 1;
  pageSize: number = 10;
  utilityId: number = 1;
  searchText: string = '';
  tariffs: Tariff[] = [];
  selectedTariff: Tariff = new Tariff();
  updatedTariff: Tariff = new Tariff();
  sltOptions: OptionObj[] = [];
  selectedSltKey: number | null = null;
  isEditMode: boolean = false;

  selectedServiceTypes: ServiceType[] = [];
  selectedCustomerTypes: CustomerType[] = [];
  selectedUseOfPremises: BuildingType[] = [];

  serviceTypeItems = this.getServiceTypeItems();
  customerTypeItems = this.getCustomerTypeItems();
  useOfPremisesItems = this.getUseOfPremisesItems();

  @Output() filterTemplateReady = new EventEmitter<TemplateRef<any>>();

  @Input() set refreshTrigger(_: number) {
    this.filterTemplateReady.emit(this.filterTemplate);
  }

  @ViewChild('closeEditModal') closeEditModal: ElementRef;

  constructor(
    private tariffService: TariffService,
    private toastr: ToastrService,
    private customerService: CustomerService,
    private utilitySharedService: UtilitySharedService
  ) {
    this.utilityId = Number(this.utilitySharedService.getUtilityId());
  }

  ngOnInit(): void {
    this.filterTemplateReady.emit(this.filterTemplate);
    this.getSltStatuses();
    this.reloadTable(1);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadTableInput']) {
      setTimeout(() => {
        this.reloadTable(1);
      }, 500);
    }
  }

  openAddModal() {
    this.isEditMode = false;
    this.selectedTariff = new Tariff();
    this.updatedTariff = new Tariff();
    this.selectedSltKey = null;
    this.selectedServiceTypes = [];
    this.selectedCustomerTypes = [];
    this.selectedUseOfPremises = [];
    this.serviceTypeItems = this.getServiceTypeItems();
    this.customerTypeItems = this.getCustomerTypeItems();
    this.useOfPremisesItems = this.getUseOfPremisesItems();
  }

  openEditModal() {
    this.isEditMode = true;
    this.fillInputFields();
  }

  getCustomerTypeItems() {
    return Object.entries(CustomerType)
      .filter(([key, val]) => typeof val === 'number')
      .map(([name, id]) => ({ id: id as CustomerType, name, checked: false }));
  }

  getUseOfPremisesItems() {
    return Object.entries(BuildingType)
      .filter(([key, val]) => typeof val === 'number')
      .map(([name, id]) => ({ id: id as BuildingType, name, checked: false }));
  }

  getServiceTypeItems() {
    return Object.entries(ServiceType)
      .filter(([key, val]) => typeof val === 'number')
      .map(([name, id]) => ({ id: id as ServiceType, name, checked: false }));
  }

  onCustomerTypesSelected(selectedNames: string[]) {
    this.selectedCustomerTypes = selectedNames
      .map((name) => CustomerType[name as keyof typeof CustomerType])
      .filter((val): val is CustomerType => typeof val === 'number');
    this.updatedTariff.customerTypes = [...this.selectedCustomerTypes];
  }

  onUseOfPremisesSelected(selectedNames: string[]) {
    this.selectedUseOfPremises = selectedNames
      .map((name) => BuildingType[name as keyof typeof BuildingType])
      .filter((val): val is BuildingType => typeof val === 'number');
    this.updatedTariff.useOfPremises = [...this.selectedUseOfPremises];
  }

  onServiceTypesSelected(selectedNames: string[]) {
    this.selectedServiceTypes = selectedNames
      .map((name) => ServiceType[name as keyof typeof ServiceType])
      .filter((val): val is ServiceType => typeof val === 'number');
    this.updatedTariff.serviceTypes = [...this.selectedServiceTypes];
  }

  selectTariff(tariff: Tariff) {
    this.selectedTariff = tariff;
    this.openEditModal();
  }

  onDeleteTariff(tariff: Tariff){
    this.selectedTariff = tariff;
    this.isEditMode = false;
  }

  private fillInputFields() {
    this.updatedTariff = { ...this.selectedTariff };
    this.selectedSltKey = this.findSltById(this.selectedTariff.sltStatusId)?.key ?? null;

    const selectedServiceTypeValues = (this.selectedTariff.serviceTypes ?? [])
      .map((name) => ServiceType[name as unknown as keyof typeof ServiceType])
      .filter((val): val is ServiceType => typeof val === 'number');

    const selectedCustomerTypeValues = (this.selectedTariff.customerTypes ?? [])
      .map((name) => CustomerType[name as unknown as keyof typeof CustomerType])
      .filter((val): val is CustomerType => typeof val === 'number');

    const selectedUseOfPremises = (this.selectedTariff.useOfPremises ?? [])
      .map((name) => BuildingType[name as unknown as keyof typeof BuildingType])
      .filter((val): val is BuildingType => typeof val === 'number');

    this.serviceTypeItems = this.getServiceTypeItems().map((item) => ({
      ...item,
      checked: selectedServiceTypeValues.includes(item.id),
    }));

    this.customerTypeItems = this.getCustomerTypeItems().map((item) => ({
      ...item,
      checked: selectedCustomerTypeValues.includes(item.id),
    }));

    this.useOfPremisesItems = this.getUseOfPremisesItems().map((item) => ({
      ...item,
      checked: selectedUseOfPremises.includes(item.id),
    }));
  }

  private getFilterObject(): any {
    return {
      searchValue: this.searchText,
    };
  }

  reloadTable(page: number | null = null) {
    if (page) this.currentPage = page;
    let obj = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: this.getFilterObject(),
      utilityId: this.utilityId,
    };
    this.getAllTariffs(obj);
  }

  private getAllTariffs(dataIn: any): void {
    this.tariffService.getAll(dataIn).subscribe({
      next: (response) => {
        const body = response?.body ?? response;
        if (body?.status === 'OK' && body?.data?.data) {
          this.tariffs = body.data.data;
          this.tariffs.forEach((x) => {
            x.currentServiceTypes = x.serviceTypes?.map((y) => y).join(', ');
            x.currentUseOfPremises = x.useOfPremises?.map((y) => y).join(', ');
            x.currentCustomerTypes = x.customerTypes?.map((y) => y).join(', ');
          });
          this.count = body.data.count ?? 0;

          if (this.count === 0) {
            this.toastr.info('No data for given input.');
          }
        } else {
          this.toastr.warning(
            body?.message || 'Unexpected response from server.'
          );
          this.tariffs = [];
          this.count = 0;
        }
      },
      error: (error) => {
        this.toastr.error('An error occurred while fetching tariffs.');
        console.error(error);
        this.tariffs = [];
        this.count = 0;
      },
    });
  }

  getSltStatuses() {
    this.customerService.getSltStatusesForOptions(this.utilityId).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.sltOptions = responseData.data;
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  findSltById(id?: number) {
    return this.sltOptions.find((x) => x.key === id);
  }

  save() {
    if (!this.updatedTariff.name) {
      this.toastr.warning("Name can't be empty!");
      return;
    }
    if (!this.updatedTariff.class) {
      this.toastr.warning("Class can't be empty!");
      return;
    }
    if (this.updatedTariff.rate == null) {
      this.toastr.warning("Rate can't be empty!");
      return;
    }
    if (!this.selectedSltKey) {
      this.toastr.warning("Please select an SLT status!");
      return;
    }
    if ((this.updatedTariff.serviceTypes ?? []).length === 0) {
      this.toastr.warning("Please select at least one Service Type!");
      return;
    }
    if ((this.updatedTariff.customerTypes ?? []).length === 0) {
      this.toastr.warning("Please select at least one Customer Type!");
      return;
    }
    if ((this.updatedTariff.useOfPremises ?? []).length === 0) {
      this.toastr.warning("Please select at least one Use of Premises!");
      return;
    }

    this.updatedTariff.utilityId = this.utilityId;
    this.updatedTariff.sltStatusId = Number(this.selectedSltKey);
    this.updatedTariff.id = this.isEditMode ? this.selectedTariff.id : 0;

    this.tariffService.save(this.updatedTariff).subscribe({
      next: (response) => {
        if (response.body.status === 200 || response.body.status === 'OK') {
          this.toastr.success(this.isEditMode ? "Tariff successfully updated." : "Tariff successfully added.");
          this.closeEditModal?.nativeElement?.click();
          setTimeout(() => {
            this.reloadTable(1);
          }, 500);
        } else {
          this.toastr.error(response.body.message || 'Failed to save tariff');
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred while saving tariff');
      },
    });
  }

  deleteTariff() {
    this.tariffService.delete(this.selectedTariff.id).subscribe({
      next: (response) => {
        if (response.body.status === 200 || response.body.status === 'OK') {
          this.toastr.success(response.body.message);
          this.onPageChange(1);
        } else {
          this.toastr.error(response.body.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occurred');
      },
    });
  }

  onSearch() {
    this.currentPage = 1;
    let dataIn = {
      pageInfo: {
        page: this.currentPage,
        pageSize: this.pageSize,
      },
      filterParams: {
        searchValue: this.searchText,
      },
      utilityId: this.utilityId,
    };
    this.getAllTariffs(dataIn);
  }

  onSearchChange() {
    if (this.searchText === '' || this.searchText.trim() === '') {
      this.onSearch();
    }
  }

  onPageChange(newPage: number) {
    this.currentPage = newPage;
    this.reloadTable(newPage);
  }
}